import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FundSummary } from '@nemo/api';
import { formatRoundLabel, formatRoundMonth } from '../dealroom/format.js';
import { useProjectAFunds } from '../portfolio/useProjectAFunds.js';
import { useTRPC } from '../trpc.js';
import { ConfirmModal } from '../ui/ConfirmModal.js';
import { MultiSelect } from '../ui/MultiSelect.js';
import { Select } from '../ui/Select.js';
import { useToast } from '../ui/Toast.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

type CoInvestment = FundSummary['coInvestments'][number];

/**
 * Where this fund sat beside Project A, from Project A's own fund accounting:
 * the strongest reason for a follow-on conversation, so it comes before the
 * rounds. Company, how it did, and which Project A funds hold it; under each,
 * the rounds there Dealroom saw the fund on. A company only Dealroom saw it
 * in is shown hollow, as a derived sector is: evidence, not the record.
 *
 * Also where a pair the export has not caught up with is added, and a wrong
 * one taken away. What is added here is kept by the next import; what is
 * removed comes back with it if the export still carries it, and the confirm
 * says which of the two a removal is.
 */
export function CoInvestments({ fund }: { fund: FundSummary }): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<CoInvestment | null>(null);

  const refresh = (): void => {
    invalidateFundRows(queryClient, trpc);
    void queryClient.invalidateQueries({ queryKey: trpc.coInvestments.options.queryKey() });
  };

  const remove = useMutation(
    trpc.coInvestments.remove.mutationOptions({
      onSuccess: () => {
        refresh();
        toast(`${fund.name} is no longer recorded in ${removing?.company ?? 'that company'}.`);
        setRemoving(null);
      },
      onError: (error: { message: string }) => toast(error.message),
    }),
  );

  return (
    <div className="pane-coinvestments">
      <h4 className="pane-subtitle">Co-investments with Project A</h4>
      <div className="pane-list">
        {fund.coInvestments.map((row) => (
          <div
            className={row.observedOnly ? 'coinvest observed' : 'coinvest'}
            key={row.portfolioCompanyId}
          >
            <p className="round-line coinvest-row">
              <span className="round-company">{row.company}</span>
              {row.observedOnly ? (
                <span
                  className="chip-hollow"
                  title="Dealroom saw this fund on a round here; Project A's fund accounting does not record it"
                >
                  seen on Dealroom
                </span>
              ) : (
                <span className="round-part">{row.status}</span>
              )}
              <span className="spacer" />
              <span className="round-part">{row.projectAFunds.join(', ')}</span>
              {!row.observedOnly && (
                <button
                  type="button"
                  className="icon-button danger coinvest-remove"
                  aria-label={`Remove ${fund.name} from ${row.company}`}
                  title={`Remove ${fund.name} from ${row.company}`}
                  onClick={() => setRemoving(row)}
                >
                  ✕
                </button>
              )}
            </p>
            {row.observedRounds.map((round, index) => (
              <p className="round-sub" key={index}>
                {observedLine(round)}
              </p>
            ))}
          </div>
        ))}
      </div>

      {adding ? (
        <AddCoInvestment
          fundId={fund.id}
          fundName={fund.name}
          onDone={() => {
            refresh();
            setAdding(false);
          }}
          onClose={() => setAdding(false)}
        />
      ) : (
        <button className="pane-add-link" type="button" onClick={() => setAdding(true)}>
          Add co-investment
        </button>
      )}

      {removing !== null && (
        <ConfirmModal
          title={`Remove ${fund.name} from ${removing.company}?`}
          body={
            removing.manual
              ? 'Somebody added this by hand, so nothing will bring it back.'
              : "Quantium's export lists this pair, so the next import will bring it back."
          }
          confirmLabel="Remove"
          danger
          onConfirm={() =>
            remove.mutate({ fundId: fund.id, portfolioCompanyId: removing.portfolioCompanyId })
          }
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

/** "co-investor on Proxima Fusion · Series B · Jul 2026". */
function observedLine(round: CoInvestment['observedRounds'][number]): string {
  return [
    `co-investor on ${round.company}`,
    formatRoundLabel(round.roundLabel),
    round.month === null ? '' : formatRoundMonth(round.month),
  ]
    .filter((part) => part !== '')
    .join(' · ');
}

interface AddCoInvestmentProps {
  fundId: string;
  fundName: string;
  onDone: () => void;
  onClose: () => void;
}

/**
 * One company, how it is doing and who holds it. Picking a company the list
 * already has fills in what Quantium said about it, so the usual add is one
 * choice and a click; changing those two fields changes the company for every
 * fund in it, which is what they are.
 */
function AddCoInvestment(props: AddCoInvestmentProps): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const listId = useId();
  const options = useQuery(trpc.coInvestments.options.queryOptions());

  const [company, setCompany] = useState('');
  const [status, setStatus] = useState('');
  const [holdingFunds, setHoldingFunds] = useState<string[]>([]);
  const [otherFund, setOtherFund] = useState('');
  // A code that is not a Project A fund yet, waiting on "add it?".
  const [creating, setCreating] = useState<string | null>(null);
  const projectAFunds = useProjectAFunds();

  const companies = options.data?.companies ?? [];
  const known = companies.find((row) => row.name.toLowerCase() === company.trim().toLowerCase());

  const add = useMutation(
    trpc.coInvestments.add.mutationOptions({
      onSuccess: () => {
        toast(`${props.fundName} is recorded as a co-investor in ${company.trim()}.`);
        props.onDone();
      },
      onError: (error: { message: string }) => toast(error.message),
    }),
  );

  const pickCompany = (value: string): void => {
    setCompany(value);
    const match = companies.find((row) => row.name.toLowerCase() === value.trim().toLowerCase());
    if (match !== undefined) {
      setStatus(match.status);
      setHoldingFunds(match.holdingFunds);
    }
  };

  const fundChoices = [
    ...(options.data?.holdingFunds ?? []),
    ...holdingFunds.filter((code) => !(options.data?.holdingFunds ?? []).includes(code)),
  ];

  const hold = (code: string): void => {
    if (!holdingFunds.includes(code)) setHoldingFunds([...holdingFunds, code]);
  };

  // A holding is a Project A fund's code; one the list does not have yet is
  // offered as a new fund rather than refused at the save.
  const addOtherFund = (): void => {
    const code = otherFund.trim();
    if (code === '') return;
    setOtherFund('');
    if (projectAFunds.isKnown(code)) hold(code);
    else setCreating(code);
  };

  return (
    <form
      className="log-contact coinvest-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (company.trim() === '' || status === '') return;
        add.mutate({
          fundId: props.fundId,
          company: company.trim(),
          status,
          holdingFunds,
        });
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        props.onClose();
      }}
    >
      <input
        autoFocus
        list={listId}
        aria-label="Portfolio company"
        placeholder="Portfolio company"
        value={company}
        onChange={(event) => pickCompany(event.target.value)}
      />
      <datalist id={listId}>
        {companies.map((row) => (
          <option key={row.id} value={row.name} />
        ))}
      </datalist>
      {company.trim() !== '' && known === undefined && (
        <p className="subtle coinvest-hint">A new portfolio company.</p>
      )}

      <div className="log-contact-row">
        <Select
          label="Status"
          className="log-contact-person"
          value={status}
          onChange={setStatus}
          options={(options.data?.statuses ?? []).map((word) => ({ value: word, label: word }))}
          placeholder="Status"
        />
        <MultiSelect
          label="Project A funds holding it"
          noun="funds"
          placeholder="Project A funds"
          selected={holdingFunds}
          options={fundChoices.map((code) => ({ value: code, label: code }))}
          onToggle={(code) =>
            setHoldingFunds(
              holdingFunds.includes(code)
                ? holdingFunds.filter((held) => held !== code)
                : [...holdingFunds, code],
            )
          }
          onClear={() => setHoldingFunds([])}
        />
        <input
          className="coinvest-other"
          aria-label="Another Project A fund"
          placeholder="Other fund"
          value={otherFund}
          onChange={(event) => setOtherFund(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            addOtherFund();
          }}
          onBlur={addOtherFund}
        />
      </div>

      {creating !== null && (
        <ConfirmModal
          title={`Add ${creating} as a Project A fund?`}
          body="A company can only be held by one of Project A's own funds. You can name it properly on the Portfolio page."
          confirmLabel="Add fund"
          onConfirm={() => {
            const code = creating;
            setCreating(null);
            void projectAFunds.create(code).then((created) => {
              if (created) hold(code);
            });
          }}
          onCancel={() => setCreating(null)}
        />
      )}

      <div className="log-contact-row">
        <button
          className="button small primary"
          type="submit"
          disabled={add.isPending || company.trim() === '' || status === ''}
        >
          Add
        </button>
        <button className="button small" type="button" onClick={props.onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
